import React, { Component, Fragment } from 'react';
import clamp from 'lodash-es/clamp';
import findLastIndex from 'lodash-es/findLastIndex';
import forEach from 'lodash-es/forEach';
import throttle from 'lodash-es/throttle';
import styled from 'styled-components';

import { Flex } from '@rover/kibble/core';
import type { ThemeHeadingSizes } from '@rover/kibble/styles';

import { Header } from '../../components/Header';
import { type OnPaginateAction, CarouselScrollSource } from '../../utils/onPaginateEvent.types';

import { Stepper } from './Stepper';

/**
 * @deprecated - The entire Snapping carousel components library is deprecated. Please use the new BaseCarousel or SnappingCarousel v2 for your Carousel needs. This code will be removed in DEV-128063
 */
export const OuterWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100vw;
`;

const ScrollWrapper = styled.div<{
  itemMargin: number;
}>`
  height: 100%;
  width: 100%;
  display: flex;
  flex-wrap: nowrap;
  overflow-x: scroll;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: 0 0 auto;
    max-width: calc(100% - ${(props) => props.itemMargin * 2}px);
    margin: ${(props) => props.itemMargin}px;
  }
`;
const SpacingHelper = styled.div<{
  width: number;
}>`
  width: ${(props) => props.width}px;
`;

/**
 * @deprecated - The entire Snapping carousel components library is deprecated. Please use the new BaseCarousel or SnappingCarousel v2 for your Carousel needs. This code will be removed in DEV-128063
 */
export type Props = {
  itemMargin?: number;
  heading?: string;
  headingSize?: ThemeHeadingSizes;
  subheading?: React.ReactNode;
  children: JSX.Element[];
  isLoading?: boolean;
  onScrolledToEnd?: () => void;
  onScroll?: () => void;
  onSnap?: (arg0: { index: number; userInitiated: boolean }) => void;
  looping?: boolean;
  fillFinalCard?: boolean;
  onPaginate?: OnPaginateAction;
};

/**
 * @deprecated - The entire Snapping carousel components library is deprecated. Please use the new BaseCarousel or SnappingCarousel v2 for your Carousel needs. This code will be removed in DEV-128063
 */
export type State = {
  pages: number;
  selectedPage: number;
  loopingChildren: JSX.Element[];
  spacingHelperWidth: number;
  pageScrollStartIndex: number;
  pageScrollEndIndex: number;
  isScrolling: boolean;
};

/**
 * @deprecated - The entire Snapping carousel components library is deprecated. Please use the new BaseCarousel or SnappingCarousel v2 for your Carousel needs. This code will be removed in DEV-128063
 */
export default class SnappingCarousel extends Component<Props, State> {
  scrollWrapper: HTMLDivElement | undefined;

  pagePoints: number[] | undefined;

  snapPoints: number[] | undefined;

  isScrollingTimeout: ReturnType<typeof setTimeout> | null | undefined;

  loopWidth: number | undefined;

  static defaultProps = {
    itemMargin: 8,
    isLoading: false,
    onScrolledToEnd: (): void => {},
    looping: false,
    onScroll: null,
    onSnap: null,
    fillFinalCard: true,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      pages: 1,
      selectedPage: 0,
      // first part of looping support, create 3 sets of children
      loopingChildren: props.looping
        ? [...props.children, ...props.children, ...props.children]
        : [],
      spacingHelperWidth: 50,
      pageScrollStartIndex: 1,
      pageScrollEndIndex: 1,
      isScrolling: false,
    };
  }

  componentDidMount(): void {
    // if looping, set scroll to the start of the 2nd set, track loop width
    if (this.props.looping && this.scrollWrapper) {
      const { scrollWidth } = this.scrollWrapper;
      // The beginning and end cards don't have margin, so add that in
      // to get the real width of a normal loop
      this.loopWidth = (scrollWidth + 2 * this.props.itemMargin!) / 3;
      this.scrollWrapper.scrollLeft = this.loopWidth;
    }

    this.updatePageAndSnapPoints();
    this.snapToClosestSnapPoint(false);
    // NOTE(Ryan) this helps us accommodate for OuterWrapper size changes during initial page rendering
    setTimeout(() => {
      this.updatePageAndSnapPoints();
      this.snapToClosestSnapPoint(false);
    }, 1000);
  }

  componentDidUpdate(prevProps: Props): void {
    this.updatePageAndSnapPoints();
    // if we just stopped loading, check this.doSnapOnScrollEnd
    if (prevProps.isLoading && !this.props.isLoading && this.doSnapOnScrollEnd) {
      this.doSnapOnScrollEnd = false;
      this.snapToClosestSnapPoint(false);
    }
  }

  doSnapOnScrollEnd = false;

  updatePageAndSnapPoints = (): void => {
    if (!this.scrollWrapper) return;
    // TS thinks `itemMargin` is possibly undefined, but it has a value in `defaultProps`
    const itemMargin = this.props.itemMargin!;
    const { children } = this.scrollWrapper;
    // Don't include the last child, which is the SpacingHelper
    const childWidths = [...(children as unknown as HTMLElement[])]
      .slice(0, -1)
      .map((child) => child.offsetWidth + 2 * itemMargin);
    // Always include a snap point at 0 and don't create a snap point after the last child
    const snapPoints = childWidths
      .slice(0, -1)
      .reduce((points, width, i) => [...points, points[i] + width], [0]);
    // The last child width includes the margin between it and the spacing helper,
    // so subtract that amount off to find the exact edge of the card
    const lastCardRightEdge =
      snapPoints[snapPoints.length - 1] + childWidths[childWidths.length - 1] - 2 * itemMargin;
    // page points
    // first page point is always 0
    const pagePoints = [0];

    if (snapPoints.length > 1) {
      const scrollViewWidth = this.scrollWrapper.offsetWidth;
      // The leftmost point in the carousel that we can have onscreen where the last card can still fully fit
      const minScrollLeft = lastCardRightEdge - scrollViewWidth;
      // eslint-disable-next-line consistent-return
      forEach(snapPoints, (point, i) => {
        // If all the remaining cards can fit on the last page, we're done
        if (pagePoints[pagePoints.length - 1] + scrollViewWidth >= lastCardRightEdge) {
          return false;
        }

        if (this.props.fillFinalCard && !this.props.looping && point >= minScrollLeft) {
          // If we can fit all the remaining cards on screen, add the current point as a page point and exit the loop
          pagePoints.push(point);
        } else if (
          i + 1 >= snapPoints.length ||
          snapPoints[i + 1] > pagePoints[pagePoints.length - 1] + scrollViewWidth
        ) {
          // Add the current snap point if we've reached the end of the snap points list or if
          // the next snap point is beyond the current field of view
          pagePoints.push(point);
        }
      });

      // Calculate the SpacingHelper width so that there's enough space on
      // the end of the carousel to scroll to the last page breakpoint
      if (!this.props.looping) {
        // How wide the last screen needs to be, minus how wide the last elements actually are
        const spacingHelperWidth =
          scrollViewWidth - (lastCardRightEdge - pagePoints[pagePoints.length - 1]);

        if (this.state.spacingHelperWidth !== spacingHelperWidth) {
          this.setState({
            spacingHelperWidth,
          });
        }
      }
    }

    this.snapPoints = snapPoints;
    this.pagePoints = pagePoints;
    this.setPageIndicator();
  };

  setPageIndicator = (): void => {
    if (!this.scrollWrapper || !this.pagePoints) return;
    // Take the current snap point and find the first page point either equal to or less than the snap point
    const { scrollLeft } = this.scrollWrapper;
    let selectedPage = 0;

    while (this.pagePoints[selectedPage] < scrollLeft) {
      selectedPage += 1;
    }

    // update state
    const pages = this.pagePoints.length;

    if (pages !== this.state.pages || selectedPage !== this.state.selectedPage) {
      this.setState(() => ({
        pages,
        selectedPage,
      }));
    }
  };

  throttledSetPageIndicator = throttle(this.setPageIndicator, 200, {
    leading: false,
    trailing: true,
  });

  animateToScrollPoint = (targetScrollLeft: number): void => {
    if (!this.scrollWrapper) return;
    this.scrollWrapper.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
  };

  handleSnap = (args: { index: number; userInitiated: boolean }): void => {
    const { onSnap } = this.props;
    if (onSnap) onSnap(args);
  };

  snapToClosestSnapPoint = (userInitiated: boolean): void => {
    if (!this.scrollWrapper || !this.snapPoints) return;
    // determine closest snap point to current scroll position
    const { scrollLeft } = this.scrollWrapper;
    const leftSnapPointIndex = this.findCurrentOrPreviousPointIndex(this.snapPoints, 0);
    const rightSnapPointIndex = this.findCurrentOrNextPointIndex(
      this.snapPoints,
      this.snapPoints.length - 1
    );
    const leftSnapPoint = this.snapPoints[leftSnapPointIndex];
    const rightSnapPoint = this.snapPoints[rightSnapPointIndex];
    let snapPoint;
    let pageEnd: number;
    // check if left or right point is closer
    const leftDelta = Math.abs(scrollLeft - leftSnapPoint);
    const rightDelta = Math.abs(scrollLeft - rightSnapPoint);

    if (leftDelta < rightDelta) {
      snapPoint = leftSnapPoint;
      pageEnd = leftSnapPointIndex + 1;
    } else {
      snapPoint = rightSnapPoint;
      pageEnd = rightSnapPointIndex + 1;
    }

    this.handleSnap({
      index: this.snapPoints.indexOf(snapPoint),
      userInitiated,
    });
    this.animateToScrollPoint(snapPoint);

    this.setState({ pageScrollEndIndex: pageEnd });
    this.fireEventOnSwipePaginate(this.state.pageScrollStartIndex, pageEnd);
  };

  fireEventOnSwipePaginate = (pageStart: number, pageEnd: number): void => {
    const hasValidPageChange = pageStart !== pageEnd;
    const shouldSkipIfSamePagesHaveJustBeenLogged =
      this.state.pageScrollStartIndex === pageStart && this.state.pageScrollEndIndex === pageEnd;
    if (this.props.onPaginate && hasValidPageChange && !shouldSkipIfSamePagesHaveJustBeenLogged) {
      this.props.onPaginate({ pageStart, pageEnd, scrollSource: CarouselScrollSource.SWIPE });
    }
  };

  // updates isScrolling to false when scrolling ends, and performing scrolls for this.doSnapOnScrollEnd
  setIsScrollingTimeout = (): void => {
    const IS_SCROLLING_TIMEOUT_LENGTH = 50;

    // cancel timeout if exists
    if (this.isScrollingTimeout) {
      clearTimeout(this.isScrollingTimeout);
    }

    this.isScrollingTimeout = setTimeout(() => {
      this.setState({ isScrolling: false });

      if (this.doSnapOnScrollEnd && !this.props.isLoading) {
        this.doSnapOnScrollEnd = false;
        this.snapToClosestSnapPoint(true);
      }
    }, IS_SCROLLING_TIMEOUT_LENGTH);
  };

  handlePageChange = (pageDelta: number): void => {
    if (
      this.state.isScrolling ||
      this.props.isLoading ||
      !this.scrollWrapper ||
      !this.pagePoints ||
      !this.snapPoints
    )
      return;
    const { scrollLeft } = this.scrollWrapper;
    let currentPagePointIndex = this.state.selectedPage;

    // After page points are recalculated, (e.g., after adding new children
    // to the start/end of the list) we might not be at a page point anymore.
    // find the nearest page point to the current scroll position, in the
    // opposite direction of the scroll direction, to make sure we don't
    // skip any cards in the desired direction
    if (!this.pagePoints.includes(scrollLeft)) {
      if (pageDelta > 0) {
        // Scrolling forward
        currentPagePointIndex = this.findCurrentOrPreviousPointIndex(
          this.pagePoints,
          currentPagePointIndex
        );
      } else if (pageDelta < 0) {
        // Scrolling backward
        currentPagePointIndex = this.findCurrentOrNextPointIndex(
          this.pagePoints,
          currentPagePointIndex
        );
      }
    }

    const nextPagePointIndex = clamp(currentPagePointIndex + pageDelta, 0, this.pagePoints.length);
    const nextPagePoint = this.pagePoints[nextPagePointIndex];
    this.animateToScrollPoint(nextPagePoint);
    this.handleSnap({
      index: this.snapPoints.indexOf(nextPagePoint),
      userInitiated: true,
    });

    // We update pageScrollStartIndex and pageScrollEndIndex when a user clicks a paginate button, or if they swipe to scroll.
    //
    // We add +1 here because page index values on the SnappingCarousel start at 0, but our metrics expect them to start at 1.
    //
    // While this logic could live in the metric-firing code, onPaginate is shared by both swipe and click,
    // and the <Stepper> already gets passed in this.state.selectedPage + 1 (so that it can display the page numbers correctly in the UI).
    // Changing this would require a broader refactor.
    //
    // Note: We can revisit this if we end up replacing the SnappingCarousel after the experiment.
    this.setState({
      selectedPage: nextPagePointIndex + 1,
      pageScrollStartIndex: currentPagePointIndex + 1,
      pageScrollEndIndex: nextPagePointIndex + 1,
    });
  };

  findCurrentOrNextPointIndex = (points: number[], defaultIndex: number): number => {
    if (!this.scrollWrapper) return -1;
    const { scrollLeft } = this.scrollWrapper;
    const index = points.findIndex((point) => point >= scrollLeft);
    return index === -1 ? defaultIndex : index;
  };

  findCurrentOrPreviousPointIndex = (points: number[], defaultIndex: number): number => {
    if (!this.scrollWrapper) return -1;
    const { scrollLeft } = this.scrollWrapper;
    const index = findLastIndex(points, (point) => point <= scrollLeft);
    return index === -1 ? defaultIndex : index;
  };

  handleUserScroll = (): void => {
    const { isLoading, onScrolledToEnd, onScroll, looping, children } = this.props;
    if (onScroll) onScroll();

    if (!this.state.isScrolling) {
      // Capture the starting page when scrolling begins
      // We should always begin where we end.
      // This ensure the right page numbers are logged from onPaginate when touch scrolling
      this.setState((prevState) => ({
        isScrolling: true,
        pageScrollStartIndex: prevState.pageScrollEndIndex,
      }));
    }

    // if looping, see if we need to add "loops" and adjust scroll position
    if (looping && this.scrollWrapper && this.loopWidth) {
      const { scrollLeft, scrollWidth, offsetWidth } = this.scrollWrapper;

      // is the left edge of the visible carousel within half a loop width of the start
      if (scrollLeft < this.loopWidth / 2) {
        this.setState((state) => ({
          loopingChildren: [...children, ...state.loopingChildren],
        }));
        this.scrollWrapper.scrollLeft += this.loopWidth;
      }

      // is the right edge of the visible carousel within half a loop width of the end
      if (scrollLeft > scrollWidth - offsetWidth - this.loopWidth / 2) {
        this.setState((state) => ({
          loopingChildren: [...state.loopingChildren, ...children],
        }));
        this.scrollWrapper.scrollLeft -= this.loopWidth;
      }
    }

    // if not loading or looping, and we've reached the end of the scroll area, call props.onScrolledToEnd
    if (!isLoading && !looping && this.scrollWrapper && this.pagePoints) {
      const { scrollLeft } = this.scrollWrapper;

      if (scrollLeft === this.pagePoints[this.pagePoints.length - 1] && onScrolledToEnd) {
        onScrolledToEnd();
      }
    }

    this.setIsScrollingTimeout();
    this.throttledSetPageIndicator();
  };

  handleTouchEnd = (): void => {
    if (this.state.isScrolling || this.props.isLoading) {
      this.doSnapOnScrollEnd = true;
    } else {
      this.snapToClosestSnapPoint(true);
    }
  };

  handleMouseLeave = (): void => {
    if (this.state.isScrolling || this.props.isLoading) {
      this.doSnapOnScrollEnd = true;
    } else {
      this.snapToClosestSnapPoint(true);
    }
  };

  render(): JSX.Element {
    const {
      headingSize,
      itemMargin,
      children,
      heading,
      subheading,
      // Prevent passing these props through to `OuterWrapper`
      onScroll, // eslint-disable-line @typescript-eslint/no-unused-vars
      onScrolledToEnd, // eslint-disable-line @typescript-eslint/no-unused-vars
      onSnap, // eslint-disable-line @typescript-eslint/no-unused-vars
      onPaginate,
    } = this.props;
    const { pages, selectedPage, loopingChildren, spacingHelperWidth } = this.state;
    const scrollItems = loopingChildren.length
      ? loopingChildren
      : [...children, <SpacingHelper key="spacing-helper" width={spacingHelperWidth} />];
    return (
      <OuterWrapper data-qa-id="snapping-carousel-container">
        <Flex
          alignItems="center"
          gap="5x"
          flexDirection="row"
          justifyContent={heading || subheading ? 'space-between' : 'flex-end'}
          marginBottom="5x"
          sx={{ marginLeft: `${itemMargin}px` }}
        >
          <Header heading={heading} headingSize={headingSize} subheading={subheading} />
          {/* Desktop Stepper only displays when display is wide enough */}
          <Stepper
            data-qa-id="desktop-stepper-button-container"
            display={['none', 'flex']}
            handlePageChange={this.handlePageChange}
            onClickTrackEvent={onPaginate}
            currentPage={selectedPage + 1}
            totalPages={pages}
          />
        </Flex>

        <ScrollWrapper
          ref={(node) => {
            if (node) this.scrollWrapper = node;
          }}
          // @ts-expect-error TS thinks `itemMargin` is possibly undefined, but it has a value in `defaultProps`
          itemMargin={itemMargin}
          onScroll={this.handleUserScroll}
          onTouchEnd={this.handleTouchEnd}
          onMouseLeave={this.handleMouseLeave}
        >
          {scrollItems.map((item, i) => (
            <Fragment key={i}>{item}</Fragment>
          ))}
        </ScrollWrapper>
        {/* Mobile Stepper only displays when display is narrow enough */}
        <Stepper
          data-qa-id="mobile-stepper-button-container"
          mt="4x"
          display={['flex', 'none']}
          handlePageChange={this.handlePageChange}
          onClickTrackEvent={onPaginate}
          currentPage={selectedPage + 1}
          totalPages={pages}
        />
      </OuterWrapper>
    );
  }
}
